import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, Button, Spinner } from "react-bootstrap";
import { Plus, MessageSquare, Scale } from "lucide-react";
import { Question, questionApi, describeJurisdiction } from "./api";
import {
  customerStatusLabel,
  dateLabel,
  shortDate,
  topicLabel,
} from "../Reviews/api";
import { customerWorkflow } from "../Reviews/customerWorkflow";
import "./questions.css";

// Drafts appear under All ("Finish setup"); a needed reply shows on the row.
const filters = [
  ["all", "All"],
  ["needs_action", "Needs your action"],
  ["in_review", "In review"],
  ["answered", "Answered"],
  ["archived", "Archived"],
];
export default function QuestionsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("filter") || "all";
  const filter = filters.some(([id]) => id === requested) ? requested : "all";
  const questionId = params.get("questionId");
  const status = ["draft", "archived"].includes(filter) ? filter : "all";
  const apiFilter = status === "all" ? filter : "all";
  const url = `?status=${status}&filter=${apiFilter}${questionId ? `&questionId=${encodeURIComponent(questionId)}` : ""}`;
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true),
    [more, setMore] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const paginationRequest = useRef<AbortController | null>(null);
  const [next, setNext] = useState<number | null>(null);
  useEffect(() => {
    const c = new AbortController();
    paginationRequest.current?.abort();
    setMore(false);
    setLoading(true);
    setError("");
    setQuestions([]);
    setNext(null);
    questionApi<{ questions: Question[]; nextOffset: number | null }>(
      url,
      "GET",
      undefined,
      c.signal
    )
      .then(data => {
        if (c.signal.aborted) return;
        setQuestions(data.questions);
        setNext(data.nextOffset);
      })
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => {
      c.abort();
      paginationRequest.current?.abort();
    };
  }, [url, reload]);
  // New customers see a plain list; filters appear once there is enough to sort.
  const empty = filter === "all" && !loading && !error && !questions.length;
  const filtersShown = useRef(false);
  const showFilters =
    filtersShown.current ||
    filter !== "all" ||
    questions.length >= 5 ||
    next !== null ||
    questions.some(q => q.reviews?.some(r => customerWorkflow(r).needsAction));
  if (showFilters) filtersShown.current = true;
  async function loadMore() {
    if (next === null || more) return;
    const controller = new AbortController();
    paginationRequest.current = controller;
    setMore(true);
    setError("");
    try {
      const data = await questionApi<{
        questions: Question[];
        nextOffset: number | null;
      }>(`${url}&offset=${next}`, "GET", undefined, controller.signal);
      if (controller.signal.aborted) return;
      setQuestions(old =>
        Array.from(
          new Map([...old, ...data.questions].map(q => [q.id, q])).values()
        )
      );
      setNext(data.nextOffset);
    } catch (e: any) {
      if (!controller.signal.aborted) setError(e.message);
    } finally {
      if (paginationRequest.current === controller) setMore(false);
    }
  }
  return (
    <main className="reviewed-documents rv-questions-page">
      <header className="reviewed-documents-heading">
        <div>
          <h1>My questions</h1>
          <p>Your questions, AI answers, and attorney reviews.</p>
        </div>
        {!empty && (
          <Link className="btn btn-primary" to="/questions/new">
            <Plus size={17} /> Ask a question
          </Link>
        )}
      </header>
      {showFilters && (
        <div className="question-filters" aria-label="Question filters">
          {filters.map(([key, label]) => (
            <Button
              key={key}
              aria-pressed={filter === key}
              variant={filter === key ? "dark" : "light"}
              onClick={() =>
                setParams(
                  questionId ? { questionId, filter: key } : { filter: key }
                )
              }
            >
              {label}
            </Button>
          ))}
        </div>
      )}
      {questionId && (
        <p>
          Showing one question and its review history.{" "}
          <Link to="/questions">Show all questions</Link>
        </p>
      )}
      {error && (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      )}
      {loading ? (
        <p role="status">
          <Spinner size="sm" /> Loading questions…
        </p>
      ) : error && !questions.length ? (
        <section className="reviewed-empty">
          <h2>We couldn’t load your questions</h2>
          <Button onClick={() => setReload(n => n + 1)}>Retry questions</Button>
        </section>
      ) : !questions.length ? (
        <section className="reviewed-empty">
          <MessageSquare size={36} />
          <h2>
            {filter === "needs_action"
              ? questionId
                ? "No action needed for this question"
                : "You’re all caught up"
              : filter === "all"
                ? "Your questions will appear here"
                : "No questions in this view"}
          </h2>
          <p>
            {filter === "needs_action"
              ? questionId
                ? "This question doesn’t need anything from you right now."
                : "Nothing needs your action right now. We’ll notify you when your attorney needs a reply, approval, or payment."
              : "Ask anything legal and get a free AI answer. When it matters, an attorney can check it."}
          </p>
          {filter === "needs_action" ? (
            <Link className="btn btn-outline-dark" to="/questions">
              View all questions
            </Link>
          ) : (
            <Link className="btn btn-primary" to="/questions/new">
              Ask a question
            </Link>
          )}
        </section>
      ) : (
        <section className="question-history" aria-label="Your questions">
          {questions.map(q => (
            <article className="question-history-item" key={q.id}>
              <div className="question-history-heading">
                <MessageSquare size={20} />
                <Link to={`/questions/${q.id}`}>
                  <strong>{q.title}</strong>
                  <span>
                    {topicLabel(q.topicKey)} ·{" "}
                    {describeJurisdiction(q.jurisdiction)} ·{" "}
                    {shortDate(q.lastActivityAt || q.updatedAt)}
                  </span>
                </Link>
                <Link className="question-open-chat" to={`/questions/${q.id}`}>
                  {q.status === "draft"
                    ? "Finish setup"
                    : q.status === "archived"
                      ? "View archived"
                      : "Open AI chat"}
                </Link>
              </div>
              {!!q.reviews?.length && (
                <div
                  className="question-review-history"
                  aria-label={`Attorney reviews for ${q.title}`}
                >
                  <span className="question-review-label">
                    <Scale size={14} /> Attorney review
                    {q.reviews.length === 1 ? "" : "s"}
                  </span>
                  {q.reviews.map((r, i) => (
                    <Link
                      className="question-review-link"
                      to={`/questions/${q.id}?tab=attorney&review=${r.id}`}
                      key={r.id}
                    >
                      <div>
                        <strong>{customerStatusLabel(r)}</strong>
                        <small>
                          {r.attorney_name ? `${r.attorney_name} · ` : ""}
                          {r.due_at &&
                          r.status === "in_progress" &&
                          !r.closed_at
                            ? `Answer due ${dateLabel(r.due_at)}`
                            : `Requested ${shortDate(r.created_at)}`}
                          {q.reviews!.length > 1
                            ? ` · Review ${q.reviews!.length - i}`
                            : ""}
                        </small>
                      </div>
                      {customerWorkflow(r).needsAction ? (
                        <span className="question-status">
                          {customerWorkflow(r).label}
                        </span>
                      ) : (
                        <span className="question-review-open">
                          View review →
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      )}
      {next !== null && (
        <Button
          variant="light"
          className="mt-3"
          disabled={more}
          onClick={loadMore}
        >
          {more ? "Loading…" : "Load more"}
        </Button>
      )}
    </main>
  );
}
